import React, { useState } from 'react';
import classNames from 'classnames';

import { ReactComponent as VerifiedBadgeImage } from './images/Shipped Direct.svg';

import css from './ListingVerifiedBadge.module.css';

/**
 * Verified badge shown for doorstep-verified listings.
 *
 * @param {Object} props
 * @param {string} [props.className] - Additional class names for the root element
 * @param {string} [props.rootClassName] - Override class for the root element
 * @param {string} [props.tooltipText] - Optional tooltip text shown on hover
 * @param {string} [props.label] - Accessible label when tooltip text is not provided
 * @returns {JSX.Element}
 */
const ListingVerifiedBadge = props => {
  const { className, rootClassName, tooltipText, label } = props;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const accessibleLabel = tooltipText || label;
  const hasTooltip = !!tooltipText;

  const toggleTooltip = event => {
    if (!hasTooltip) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setTooltipVisible(isVisible => !isVisible);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      toggleTooltip(event);
    } else if (event.key === 'Escape') {
      setTooltipVisible(false);
    }
  };

  return (
    <span
      className={classNames(rootClassName || css.root, className, {
        [css.withTooltip]: hasTooltip,
        [css.tooltipVisible]: tooltipVisible,
      })}
      role={accessibleLabel ? 'img' : undefined}
      aria-label={accessibleLabel}
      aria-hidden={accessibleLabel ? undefined : true}
      tabIndex={hasTooltip ? 0 : undefined}
      onClick={toggleTooltip}
      onKeyDown={handleKeyDown}
      onBlur={() => setTooltipVisible(false)}
      onMouseLeave={() => setTooltipVisible(false)}
    >
      <VerifiedBadgeImage
        className={css.icon}
        aria-hidden="true"
      />
      {hasTooltip ? <span className={css.tooltip}>{tooltipText}</span> : null}
    </span>
  );
};

export default ListingVerifiedBadge;
